import type {
  AccountingContinuity,
  AccountingExpUpdate,
  AccountingFrame,
  AccountingMesosUpdate,
  AccountingResourceUpdate,
  GameEvent,
  GameStateSnapshot,
  LiveRateSnapshot,
  MetricKey,
  TrustedLiveRateMetric,
  ValidatedObservation,
} from '@/domain/types'
import type { GamePreset } from '@/presets/types'
import { TrustedExpLedger } from './trusted-exp-ledger'

type RateMetric = 'mesos' | 'hp' | 'mp'

const HOUR_MS = 3_600_000
const MAX_TRUSTED_INTERVAL_MS: Readonly<Record<RateMetric, number>> = {
  mesos: 15_000,
  hp: 2_000,
  mp: 2_000,
}
const RESOURCE_RATIO_TOLERANCE = 0.035

interface MetricClock {
  lastSourceAt: number | null
  lastActiveAt: number | null
  pendingIntervalMs: number
  trustedDurationMs: number
  updatedAt: number | null
  hasBaseline: boolean
  available: boolean
}

interface ClockObservation {
  readonly continuity: AccountingContinuity
  readonly sourceAt: number | null
}

interface ResourcePoint {
  readonly current: number
  readonly max: number | null
  readonly sourceAt: number
}

interface PendingResourcePoint {
  readonly point: ResourcePoint
}

export interface TrustedMetricLedgerAggregate {
  readonly expGained: number | null
  readonly expTrustedDurationMs: number
  readonly expTrustedPerHour: number | null
  readonly mesosStartBalance: number | null
  readonly mesosCurrentBalance: number | null
  readonly mesosBalanceChange: number | null
  readonly mesosDelta: number | null
  readonly mesosTrustedDurationMs: number
  readonly mesosPerHour: number | null
  readonly hpConsumed: number
  readonly hpConsumedPerHour: number | null
  readonly hpRecovered: number
  readonly hpRecoveredPerHour: number | null
  readonly mpConsumed: number
  readonly mpConsumedPerHour: number | null
  readonly mpRecovered: number
  readonly mpRecoveredPerHour: number | null
}

const emptyClock = (): MetricClock => ({
  lastSourceAt: null,
  lastActiveAt: null,
  pendingIntervalMs: 0,
  trustedDurationMs: 0,
  updatedAt: null,
  hasBaseline: false,
  available: false,
})

const emptyClocks = (): Record<RateMetric, MetricClock> => ({
  mesos: emptyClock(),
  hp: emptyClock(),
  mp: emptyClock(),
})

export class TrustedMetricLedger {
  private captureGeneration = 0
  private minimumSourceAt = 0
  private latestCapturedAt = 0
  private suspended = false
  private clocks = emptyClocks()
  private readonly expLedger: TrustedExpLedger

  private mesosBaseline: number | null = null
  private mesosStartBalance: number | null = null
  private mesosCurrentBalance: number | null = null
  private mesosDelta = 0
  private hasMesosBaseline = false
  private mesosContinuityId: number | null = null

  private resourceBaselines: Record<'hp' | 'mp', ResourcePoint | null> = { hp: null, mp: null }
  private resourcePending: Record<'hp' | 'mp', PendingResourcePoint | null> = { hp: null, mp: null }
  private resourceLastNumericAt: Record<'hp' | 'mp', number | null> = { hp: null, mp: null }
  private hasResourceBaseline: Record<'hp' | 'mp', boolean> = { hp: false, mp: false }
  private totals = { hpConsumed: 0, hpRecovered: 0, mpConsumed: 0, mpRecovered: 0 }

  constructor(preset: GamePreset) {
    void preset
    this.expLedger = new TrustedExpLedger()
  }

  reset(at: number, captureGeneration = 0, initialState: GameStateSnapshot | null = null): void {
    this.captureGeneration = captureGeneration
    this.minimumSourceAt = at
    this.latestCapturedAt = at
    this.suspended = false
    this.clocks = emptyClocks()
    this.expLedger.reset(at, initialState)
    this.mesosBaseline = null
    this.mesosStartBalance = null
    this.mesosCurrentBalance = null
    this.mesosDelta = 0
    this.hasMesosBaseline = false
    this.mesosContinuityId = null
    this.resourceBaselines = { hp: null, mp: null }
    this.resourcePending = { hp: null, mp: null }
    this.resourceLastNumericAt = { hp: null, mp: null }
    this.hasResourceBaseline = { hp: false, mp: false }
    this.totals = { hpConsumed: 0, hpRecovered: 0, mpConsumed: 0, mpRecovered: 0 }
    this.seedInitialState(initialState)
  }

  suspend(): void {
    this.suspended = true
    this.expLedger.suspend()
    this.rebaselineMetrics(false)
  }

  resume(at: number, captureGeneration: number): void {
    this.suspended = false
    if (captureGeneration !== this.captureGeneration) {
      this.captureGeneration = captureGeneration
      this.minimumSourceAt = at
      this.rebaselineMetrics(false)
    }
    this.expLedger.resume(at)
  }

  changeGeneration(at: number, captureGeneration: number): void {
    if (captureGeneration === this.captureGeneration) return
    this.captureGeneration = captureGeneration
    this.minimumSourceAt = at
    this.expLedger.changeGeneration(at)
    this.rebaselineMetrics(false)
  }

  ingest(
    state: GameStateSnapshot,
    events: readonly GameEvent[],
    observation: ValidatedObservation | undefined,
    activeAt: number,
    captureGeneration: number,
  ): AccountingFrame {
    this.changeGeneration(state.capturedAt, captureGeneration)
    this.latestCapturedAt = Math.max(this.latestCapturedAt, state.capturedAt)
    if (this.suspended) return this.emptyFrame(state.capturedAt)

    const exp = this.expLedger.ingest(state, events, observation, activeAt)
    const mesos = this.ingestMesos(state, observation, activeAt)
    const hp = this.ingestResource('hp', state, observation, activeAt)
    const mp = this.ingestResource('mp', state, observation, activeAt)
    return { capturedAt: state.capturedAt, captureGeneration: this.captureGeneration, exp, mesos, hp, mp }
  }

  aggregate(): TrustedMetricLedgerAggregate {
    const exp = this.expLedger.aggregate()
    const mesosBalanceChange = this.mesosStartBalance === null || this.mesosCurrentBalance === null
      ? null
      : this.mesosCurrentBalance - this.mesosStartBalance
    return {
      expGained: exp.gained,
      expTrustedDurationMs: exp.trustedDurationMs,
      expTrustedPerHour: exp.perHour,
      mesosStartBalance: this.mesosStartBalance,
      mesosCurrentBalance: this.mesosCurrentBalance,
      mesosBalanceChange,
      mesosDelta: this.hasMesosBaseline ? this.mesosDelta : null,
      mesosTrustedDurationMs: this.clocks.mesos.trustedDurationMs,
      mesosPerHour: this.rate(this.mesosDelta, this.clocks.mesos.trustedDurationMs, this.hasMesosBaseline),
      hpConsumed: this.totals.hpConsumed,
      hpConsumedPerHour: this.rate(this.totals.hpConsumed, this.clocks.hp.trustedDurationMs, this.hasResourceBaseline.hp),
      hpRecovered: this.totals.hpRecovered,
      hpRecoveredPerHour: this.rate(this.totals.hpRecovered, this.clocks.hp.trustedDurationMs, this.hasResourceBaseline.hp),
      mpConsumed: this.totals.mpConsumed,
      mpConsumedPerHour: this.rate(this.totals.mpConsumed, this.clocks.mp.trustedDurationMs, this.hasResourceBaseline.mp),
      mpRecovered: this.totals.mpRecovered,
      mpRecoveredPerHour: this.rate(this.totals.mpRecovered, this.clocks.mp.trustedDurationMs, this.hasResourceBaseline.mp),
    }
  }

  liveRates(_now = this.latestCapturedAt): Omit<LiveRateSnapshot, 'expPerHour'> {
    return {
      expTrustedPerHour: this.expLedger.liveRate(),
      mesosPerHour: this.liveMetric(this.mesosDelta, this.clocks.mesos),
      hpConsumedPerHour: this.liveMetric(this.totals.hpConsumed, this.clocks.hp),
      hpRecoveredPerHour: this.liveMetric(this.totals.hpRecovered, this.clocks.hp),
      mpConsumedPerHour: this.liveMetric(this.totals.mpConsumed, this.clocks.mp),
      mpRecoveredPerHour: this.liveMetric(this.totals.mpRecovered, this.clocks.mp),
    }
  }

  private ingestMesos(
    state: GameStateSnapshot,
    observation: ValidatedObservation | undefined,
    activeAt: number,
  ): AccountingMesosUpdate {
    if (observation && !observation.accepted.mesos) {
      if (observation.mesos.availability === 'unavailable') {
        this.markUnavailable('mesos')
        this.mesosBaseline = null
        return this.emptyMesosUpdate('unavailable')
      }
      return this.emptyMesosUpdate('pending', observation.mesos.capturedAt)
    }
    if (state.mesos === null) return this.emptyMesosUpdate('ignored')
    const sourceAt = this.metricObservedAt(state, observation, 'mesos')
    if (sourceAt === null || sourceAt < this.minimumSourceAt) return this.emptyMesosUpdate('ignored', sourceAt)
    const continuityId = observation?.mesosContinuityId
    if (continuityId !== undefined
      && (!Number.isSafeInteger(continuityId)
        || continuityId < 0
        || (this.mesosContinuityId !== null && continuityId < this.mesosContinuityId))) {
      return this.emptyMesosUpdate('ignored', sourceAt)
    }
    if (continuityId !== undefined && continuityId !== this.mesosContinuityId) {
      this.mesosContinuityId = continuityId
      this.markUnavailable('mesos')
      this.mesosBaseline = state.mesos
      this.mesosStartBalance ??= state.mesos
      this.mesosCurrentBalance = state.mesos
      this.hasMesosBaseline = true
      this.resetClockBaseline('mesos', sourceAt, activeAt)
      return this.emptyMesosUpdate('baseline', sourceAt, state.mesos)
    }
    const clockObservation = this.observeClock('mesos', sourceAt, activeAt)
    if (clockObservation.continuity === 'ignored') return this.emptyMesosUpdate('ignored', sourceAt)
    if (clockObservation.continuity === 'baseline' || clockObservation.continuity === 'discontinuous' || this.mesosBaseline === null) {
      this.mesosBaseline = state.mesos
      this.mesosStartBalance ??= state.mesos
      this.mesosCurrentBalance = state.mesos
      this.hasMesosBaseline = true
      return this.emptyMesosUpdate(clockObservation.continuity, sourceAt, state.mesos)
    }

    const delta = state.mesos - this.mesosBaseline
    const startValue = this.mesosBaseline
    this.mesosBaseline = state.mesos
    this.mesosCurrentBalance = state.mesos
    this.mesosDelta += delta
    const trustedIntervalMs = this.commitClock('mesos')
    return {
      continuity: 'contiguous',
      sourceAt,
      trustedIntervalMs,
      delta,
      startValue,
      endValue: state.mesos,
    }
  }

  private ingestResource(
    metric: 'hp' | 'mp',
    state: GameStateSnapshot,
    observation: ValidatedObservation | undefined,
    activeAt: number,
  ): AccountingResourceUpdate {
    if (observation && !observation.accepted[metric]) {
      if (observation[metric].availability === 'unavailable') {
        this.markUnavailable(metric)
        this.resourceBaselines[metric] = null
        this.resourcePending[metric] = null
        this.resourceLastNumericAt[metric] = null
        return this.emptyResourceUpdate('unavailable')
      }
      return this.emptyResourceUpdate('pending', observation[metric].capturedAt)
    }
    const value = state[metric]
    if (value.current === null && value.ratio === null) return this.emptyResourceUpdate('ignored')

    const observedReading = observation?.[metric]
    const observedValue = observedReading?.value
    const hasVisibleBar = observedReading !== undefined
      && (observedReading.source === 'bar' || observedReading.source === 'fused')
      && observedValue?.ratio !== null
    const timelineAt = hasVisibleBar
      ? state.capturedAt
      : this.metricObservedAt(state, observation, metric)
    if (timelineAt === null) return this.emptyResourceUpdate('ignored')
    const clockObservation = this.observeClock(metric, timelineAt, activeAt)
    if (clockObservation.continuity === 'ignored') return this.emptyResourceUpdate('ignored', timelineAt)
    if (clockObservation.continuity === 'baseline' || clockObservation.continuity === 'discontinuous') {
      this.resourceBaselines[metric] = null
      this.resourcePending[metric] = null
      this.resourceLastNumericAt[metric] = null
    }
    this.hasResourceBaseline[metric] = true

    const numericCurrent = observation ? observedValue?.current ?? null : value.current
    const numericMax = observation ? observedValue?.max ?? value.max : value.max
    const numericAt = numericCurrent === null ? null : this.metricObservedAt(state, observation, metric)
    if (numericCurrent === null || numericAt === null || numericAt < this.minimumSourceAt) {
      const trustedIntervalMs = this.commitContiguousClock(metric, clockObservation.continuity)
      return this.emptyResourceUpdate(clockObservation.continuity, timelineAt, trustedIntervalMs)
    }
    if (this.resourceLastNumericAt[metric] !== null && numericAt <= this.resourceLastNumericAt[metric]!) {
      const trustedIntervalMs = this.commitContiguousClock(metric, clockObservation.continuity)
      return this.emptyResourceUpdate(clockObservation.continuity, timelineAt, trustedIntervalMs)
    }
    this.resourceLastNumericAt[metric] = numericAt
    const point: ResourcePoint = { current: numericCurrent, max: numericMax, sourceAt: numericAt }
    const baseline = this.resourceBaselines[metric]
    if (!baseline) {
      const trustedIntervalMs = this.commitContiguousClock(metric, clockObservation.continuity)
      this.resourceBaselines[metric] = point
      this.resourcePending[metric] = null
      return this.emptyResourceUpdate(clockObservation.continuity, timelineAt, trustedIntervalMs)
    }
    if (baseline.max !== null && point.max !== null && baseline.max !== point.max) {
      this.discardClockPending(metric)
      this.resourceBaselines[metric] = point
      this.resourcePending[metric] = null
      return this.emptyResourceUpdate('discontinuous', timelineAt)
    }

    const trustedIntervalMs = this.commitContiguousClock(metric, clockObservation.continuity)
    const delta = point.current - baseline.current
    if (delta === 0) {
      this.resourcePending[metric] = null
      this.resourceBaselines[metric] = point
      return this.emptyResourceUpdate(clockObservation.continuity, timelineAt, trustedIntervalMs)
    }

    const observedSource = observedReading?.source
    const sourcesAgree = observedSource !== undefined
      && this.resourceSourcesAgree(numericCurrent, numericMax, observedValue?.ratio ?? value.ratio, observedSource)
    if (observedSource === 'fused' && !sourcesAgree) {
      this.resourcePending[metric] = null
      return this.emptyResourceUpdate('pending', timelineAt, trustedIntervalMs)
    }
    const strongEvidence = observation === undefined || sourcesAgree
    if (!strongEvidence) {
      const pending = this.resourcePending[metric]
      if (!pending || !this.candidateProgresses(baseline.current, pending.point.current, point.current)) {
        this.resourcePending[metric] = { point }
        return this.emptyResourceUpdate('pending', timelineAt, trustedIntervalMs)
      }
    }

    this.resourcePending[metric] = null
    this.resourceBaselines[metric] = point
    const consumed = delta < 0 ? Math.abs(delta) : 0
    const recovered = delta > 0 ? delta : 0
    if (metric === 'hp') {
      this.totals.hpConsumed += consumed
      this.totals.hpRecovered += recovered
    } else {
      this.totals.mpConsumed += consumed
      this.totals.mpRecovered += recovered
    }
    return { continuity: 'contiguous', sourceAt: timelineAt, trustedIntervalMs, consumed, recovered }
  }

  private observeClock(metric: RateMetric, sourceAt: number, activeAt: number): ClockObservation {
    const clock = this.clocks[metric]
    clock.available = true
    clock.updatedAt = sourceAt
    if (!clock.hasBaseline || clock.lastSourceAt === null || clock.lastActiveAt === null) {
      clock.hasBaseline = true
      clock.lastSourceAt = sourceAt
      clock.lastActiveAt = activeAt
      clock.pendingIntervalMs = 0
      return { continuity: 'baseline', sourceAt }
    }
    if (sourceAt <= clock.lastSourceAt) return { continuity: 'ignored', sourceAt }
    if (sourceAt - clock.lastSourceAt > MAX_TRUSTED_INTERVAL_MS[metric]) {
      clock.lastSourceAt = sourceAt
      clock.lastActiveAt = activeAt
      clock.pendingIntervalMs = 0
      return { continuity: 'discontinuous', sourceAt }
    }
    clock.pendingIntervalMs += Math.max(0, activeAt - clock.lastActiveAt)
    clock.lastSourceAt = sourceAt
    clock.lastActiveAt = activeAt
    return { continuity: 'contiguous', sourceAt }
  }

  private commitClock(metric: RateMetric): number {
    const clock = this.clocks[metric]
    const interval = Math.max(0, clock.pendingIntervalMs)
    clock.trustedDurationMs += interval
    clock.pendingIntervalMs = 0
    return interval
  }

  private commitContiguousClock(metric: RateMetric, continuity: AccountingContinuity): number {
    return continuity === 'contiguous' ? this.commitClock(metric) : 0
  }

  private discardClockPending(metric: RateMetric): void {
    this.clocks[metric].pendingIntervalMs = 0
  }

  private markUnavailable(metric: RateMetric): void {
    const clock = this.clocks[metric]
    clock.lastSourceAt = null
    clock.lastActiveAt = null
    clock.pendingIntervalMs = 0
    clock.hasBaseline = false
    clock.available = false
  }

  private rebaselineMetrics(clearAvailability: boolean): void {
    for (const metric of Object.keys(this.clocks) as RateMetric[]) {
      const clock = this.clocks[metric]
      clock.lastSourceAt = null
      clock.lastActiveAt = null
      clock.pendingIntervalMs = 0
      clock.hasBaseline = false
      if (clearAvailability) clock.available = false
    }
    this.mesosBaseline = null
    this.mesosContinuityId = null
    this.resourceBaselines = { hp: null, mp: null }
    this.resourcePending = { hp: null, mp: null }
    this.resourceLastNumericAt = { hp: null, mp: null }
  }

  private seedInitialState(state: GameStateSnapshot | null): void {
    if (!state) return
    const mesosSourceAt = state.observedAt?.mesos ?? state.capturedAt
    if (state.mesos !== null && Number.isSafeInteger(state.mesos) && state.mesos >= 0) {
      this.mesosBaseline = state.mesos
      this.mesosStartBalance = state.mesos
      this.mesosCurrentBalance = state.mesos
      this.hasMesosBaseline = true
      this.resetClockBaseline('mesos', mesosSourceAt, 0)
    }
  }

  private resetClockBaseline(metric: RateMetric, sourceAt: number, activeAt: number): void {
    const clock = this.clocks[metric]
    clock.lastSourceAt = sourceAt
    clock.lastActiveAt = activeAt
    clock.pendingIntervalMs = 0
    clock.hasBaseline = true
    clock.available = true
    clock.updatedAt = sourceAt
  }

  private candidateProgresses(baseline: number, pending: number, current: number): boolean {
    const pendingDirection = Math.sign(pending - baseline)
    const currentDirection = Math.sign(current - baseline)
    return pendingDirection !== 0
      && currentDirection === pendingDirection
      && Math.abs(current - baseline) >= Math.abs(pending - baseline)
  }

  private resourceSourcesAgree(current: number, max: number | null, ratio: number | null, source: string): boolean {
    if (source !== 'fused' || max === null || max <= 0 || ratio === null) return false
    return Math.abs(current / max - ratio) <= RESOURCE_RATIO_TOLERANCE
  }

  private metricObservedAt(
    state: GameStateSnapshot,
    observation: ValidatedObservation | undefined,
    metric: MetricKey,
  ): number | null {
    if (observation) return observation[metric].capturedAt
    return state.observedAt?.[metric] ?? state.capturedAt
  }

  private liveMetric(total: number, clock: MetricClock, durationMs = clock.trustedDurationMs): TrustedLiveRateMetric {
    if (!clock.available) {
      return { value: null, status: 'unavailable', trustedDurationMs: durationMs, updatedAt: clock.updatedAt }
    }
    const value = durationMs > 0 ? total / durationMs * HOUR_MS : 0
    return {
      value: Number.isFinite(value) ? value : 0,
      status: 'ready',
      trustedDurationMs: durationMs,
      updatedAt: clock.updatedAt,
    }
  }

  private rate(total: number, durationMs: number, hasBaseline: boolean): number | null {
    if (!hasBaseline) return null
    const value = durationMs > 0 ? total / durationMs * HOUR_MS : 0
    return Number.isFinite(value) ? value : 0
  }

  private emptyFrame(capturedAt: number): AccountingFrame {
    return {
      capturedAt,
      captureGeneration: this.captureGeneration,
      exp: this.emptyExpUpdate('ignored'),
      mesos: this.emptyMesosUpdate('ignored'),
      hp: this.emptyResourceUpdate('ignored'),
      mp: this.emptyResourceUpdate('ignored'),
    }
  }

  private emptyExpUpdate(continuity: AccountingContinuity): AccountingExpUpdate {
    return {
      continuity,
      sourceAt: null,
      sourceStartAt: null,
      sourceEndAt: null,
      activeStartAt: null,
      activeEndAt: null,
      trustedIntervalMs: 0,
      delta: null,
      exact: false,
      start: null,
      end: null,
    }
  }

  private emptyMesosUpdate(
    continuity: AccountingContinuity,
    sourceAt: number | null = null,
    value: number | null = null,
  ): AccountingMesosUpdate {
    return { continuity, sourceAt, trustedIntervalMs: 0, delta: null, startValue: null, endValue: value }
  }

  private emptyResourceUpdate(
    continuity: AccountingContinuity,
    sourceAt: number | null = null,
    trustedIntervalMs = 0,
  ): AccountingResourceUpdate {
    return { continuity, sourceAt, trustedIntervalMs, consumed: 0, recovered: 0 }
  }
}
